/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled/macro'; // for dom selector
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from 'component/common/block/text-block';
import { formatDate, removeEmailDomain, removePrefix } from '@util/string';
import IconBlock from 'component/common/block/icon-block';
import { useModifyShelfMutation } from '@hook/moashelf/useApi';
import { actions as newsletterActions } from '@store/slice/newsletter-slice';
import { useTypedDispatch } from '@hook/common/useStore';
import Line from '@component/common/line';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MOASHELF_PATH } from '@constant/moashelf/route-path';
import Gap from '@component/common/gap';

interface IProps {
  shelfKey: string;
  title: string;
  publishedAt: string;
  provider: string;
  isBookmark: boolean;
  readAt: string;
  trashedAt: string;
}

// WebSubscriptionItemContainer 똑같이
const WebShelfItemContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  margin: 0 auto 0 auto;
  width: 100%;
  height: 50px;
  padding: 0 ${size.MARGIN_2} 0 ${size.MARGIN_2};
`;

const MobileShelfItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  /* width: calc(100% - 2 * ${size.MARGIN_2}); */
  width: 100%;
  height: 80px;
  padding: 0 ${size.MARGIN_1} 0 ${size.MARGIN_1};

  background-color: ${color.WHITE_1};
`;

const DeleteContainer = styled.div`
  display: none;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
`;

// TODO: 포커싱되면 뽈록뽈록
const Wrapper = styled.div`
  cursor: pointer;

  &:hover {
    box-shadow: ${IS_IYAGIGO_MOBILE_VIEW ? 'none' : color.SURFACE_SHADOW};
  }

  &:hover ${DeleteContainer} {
    display: flex;
  }

  margin: 0 auto 0 auto;
`;

const MobileRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ShelfItem: React.FC<IProps> = ({ shelfKey, title, publishedAt, provider, isBookmark, readAt, trashedAt }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const modifyShelfMutation = useModifyShelfMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleTitleClick = (shelfKey: string) => {
    // dispatch(newsletterActions.clickShelfItem(shelfKey));
    // setSearchParams({ shelfKey: shelfKey });
    navigate(`${MOASHELF_PATH.SHELF_PATH}/${shelfKey}`);
  };

  const handleBookmarkClick = async (ev) => {
    ev.stopPropagation();
    await modifyShelfMutation.mutateAsync({ shelfKey, isBookmark: !isBookmark });
  };

  const handleTrashClick = async (ev) => {
    ev.stopPropagation();
    await modifyShelfMutation.mutateAsync({ shelfKey, isTrash: true });
  };

  const handleUntrashClick = async (ev) => {
    ev.stopPropagation();
    await modifyShelfMutation.mutateAsync({ shelfKey, isTrash: false });
  };

  const MOBILE_TEXT_DESIGN = {
    text: { size: 'medium', bold: true, color: !readAt ? color.BLACK_2 : color.GRAY_1 },
    box: { alignX: 'left' },
  } as const;

  const TEXT_DESIN = {
    text: { size: 'medium', color: !readAt ? color.BLACK_2 : color.GRAY_1 },
    box: { alignX: 'left' },
  } as const;

  const ICON_DESIGN = {
    icon: { size: 'medium', color: color.GRAY_2, kind: 'outlined' },
    box: { width: '30px' },
  } as const;

  return (
    <Wrapper>
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <MobileShelfItemContainer onClick={() => handleTitleClick(shelfKey)}>
          <MobileRowContainer>
            <TextBlock text={provider} design={MOBILE_TEXT_DESIGN} />
            <TextBlock
              text={formatDate(publishedAt)}
              design={{
                box: TEXT_DESIN.box,
                text: { ...TEXT_DESIN.text, size: 'tiny', bold: true },
              }}
            />
          </MobileRowContainer>
          <MobileRowContainer>
            <TextBlock
              text={removePrefix(title, '[Catch] ')}
              design={{
                text: { ...TEXT_DESIN.text, oneline: true },
                box: { ...TEXT_DESIN.box, width: 'calc(100% - 60px)' },
              }}
            />
            <IconBlock
              icon='star'
              design={{
                box: ICON_DESIGN.box,
                icon: { ...ICON_DESIGN.icon, checkedColor: color.YELLOW_1, checked: isBookmark },
              }}
              click={{ handler: handleBookmarkClick }}
            />
            <IconBlock icon='trash' design={ICON_DESIGN} click={{ handler: handleTrashClick }} />
          </MobileRowContainer>
        </MobileShelfItemContainer>
      ) : (
        <WebShelfItemContainer>
          <TextBlock
            text={removeEmailDomain(provider)}
            design={{
              box: { ...TEXT_DESIN.box, width: '120px' },
              text: { ...TEXT_DESIN.text, oneline: true },
            }}
          />
          <IconBlock
            icon='star'
            design={{
              box: { ...ICON_DESIGN.box, margin: `0 ${size.MARGIN_1} 0 ${size.MARGIN_1}` },
              icon: { ...ICON_DESIGN.icon, checkedColor: color.YELLOW_1, checked: isBookmark },
            }}
            click={{ handler: handleBookmarkClick }}
          />
          <TextBlock
            text={removePrefix(title, '[Catch] ')}
            design={{
              box: { ...TEXT_DESIN.box, width: 'calc(100% - 250px)' },
              text: { ...TEXT_DESIN.text, oneline: true },
            }}
            click={{ handler: () => handleTitleClick(shelfKey) }}
          />
          <DeleteContainer>
            {trashedAt ? (
              <IconBlock icon='untrash' design={ICON_DESIGN} click={{ handler: handleUntrashClick }} />
            ) : (
              <IconBlock icon='trash' design={ICON_DESIGN} click={{ handler: handleTrashClick }} />
            )}
          </DeleteContainer>
          <TextBlock
            text={formatDate(publishedAt)}
            design={{
              text: TEXT_DESIN.text,
              box: { ...TEXT_DESIN.box, width: '80px', alignX: 'center', margin: '0px 0px 0px auto' },
            }}
          />
        </WebShelfItemContainer>
      )}
      <Line width='100%' color={color.GRAY_3} />
    </Wrapper>
  );
};

// 저장하기, 메모
export default ShelfItem;
